import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { mdy } from "@/lib/dateFmt";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PhoneCall, AlertTriangle, Send, Clock, Info } from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  { id: "sick", label: "Sick", desc: "Not feeling well" },
  { id: "fmla", label: "FMLA", desc: "Approved FMLA leave" },
  { id: "other", label: "Other", desc: "Something else — add a comment" },
];

/**
 * Two-step call-out flow.
 * STEP 1: mandatory — call the on-call manager first (no ability to skip)
 * STEP 2: submit the absent form once the call has been made
 */
export default function CallOutDialog({ open, onClose, facilityId, today, oncall, onSubmitted }) {
  const [step, setStep] = useState(1);
  const [called, setCalled] = useState(false);
  const [reason, setReason] = useState("sick");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    setStep(1); setCalled(false); setReason("sick"); setComment("");
    onClose();
  };

  const canSubmitStep2 = called && (reason !== "other" || comment.trim().length > 0);

  const submit = async () => {
    if (!today?.date) { toast.error("You don't have a shift today"); return; }
    setSaving(true);
    try {
      await api.post("/callouts", {
        facility_id: facilityId,
        date: today.date,
        reason,
        comment: comment.trim() || null,
      });
      onSubmitted?.();
      handleClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't submit");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle size={18} /> Call out — {step === 1 ? "Step 1 of 2" : "Step 2 of 2"}
          </DialogTitle>
        </DialogHeader>

        {/* Blocking banner: 2-hour warning */}
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2" data-testid="callout-warning">
          <Clock size={14} className="mt-0.5 shrink-0" />
          <div>
            <b>Call-outs require 2 hours notice.</b> Please call your on-call manager as early as possible.
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                <Info size={12} /> Step 1 — Call the on-call manager
              </div>
              <div className="text-sm text-slate-800 mt-1.5 leading-snug">
                You <b>must</b> call before submitting. This form does not replace the phone call.
              </div>
              {oncall ? (
                <div className="mt-3 flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full grid place-items-center text-white font-bold text-sm shrink-0"
                    style={{ background: oncall.color || "#64748b" }}
                  >
                    {oncall.initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{oncall.name}</div>
                    <div className="text-xs text-slate-500 tabular-nums">{oncall.phone}</div>
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-xs text-red-700">
                  No on-call manager assigned today — contact your direct manager.
                </div>
              )}
              {oncall && (
                <a
                  href={`tel:${(oncall.phone || "").replace(/[^\d+]/g, "")}`}
                  className="mt-3 inline-flex items-center justify-center w-full gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 px-3 transition-colors"
                  onClick={() => setCalled(true)}
                  data-testid="callout-call-btn"
                >
                  <PhoneCall size={14} /> Call {oncall.name.split(" ")[0]}
                </a>
              )}
              <label className="mt-3 flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={called}
                  onChange={(e) => setCalled(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 accent-emerald-600"
                  data-testid="callout-called-check"
                />
                I have called the on-call manager.
              </label>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="callout-step2">
            <div>
              <Label className="text-sm font-semibold">Reason</Label>
              <div className="mt-2 space-y-2">
                {REASONS.map((r) => (
                  <label
                    key={r.id}
                    className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer transition ${
                      reason === r.id ? "border-red-500 bg-red-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`callout-reason-${r.id}`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={r.id}
                      checked={reason === r.id}
                      onChange={() => setReason(r.id)}
                      className="mt-0.5 accent-red-600"
                    />
                    <div className="min-w-0 text-sm">
                      <div className="font-semibold text-slate-900">{r.label}</div>
                      <div className="text-xs text-slate-500">{r.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="callout-comment" className="text-sm font-semibold">
                Comment {reason === "other" && <span className="text-red-600">*</span>}
              </Label>
              <Textarea
                id="callout-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder={reason === "other" ? "Required — briefly explain the reason" : "Optional — anything your manager should know"}
                className="mt-1"
                data-testid="callout-comment"
              />
            </div>

            {today?.shift_code && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600">
                Submitting will mark your <b>{today.shift_code}</b> shift on {mdy(today.date)} as absent and move it to open shifts.
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="callout-cancel">Cancel</Button>
          {step === 1 ? (
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!called}
              onClick={() => setStep(2)}
              data-testid="callout-next"
            >
              I've called — continue to Step 2
            </Button>
          ) : (
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!canSubmitStep2 || saving || !today?.date}
              onClick={submit}
              data-testid="callout-submit"
            >
              <Send size={14} className="mr-1" /> {saving ? "Submitting…" : "Submit absent"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
